import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Loader2,
  Receipt,
} from "lucide-react";
import { useOnlinePayment } from "../hooks/useOnlinePayment";
import { useOrderStatus } from "../contexts/OrderStatusContext";
import {
  ONLINE_PAYMENT_STATUS,
  FINAL_STATUSES,
  RETRYABLE_STATUSES,
  getLastOnlinePayment,
  parseReturnParams,
  saveLastOnlinePayment,
} from "../lib/onlinePayment";

const formatBRL = (n) =>
  Number(n || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 2,
  });

// Polling de GET /online-payments/{paymentId}: a cada 4 s por até ~3 min
const POLL_INTERVAL_MS = 4000;
const MAX_POLLS = 45;

/**
 * Página de retorno do pagamento online (PAYMENT_CHECKOUT_RETURN_URL do back).
 * Recebe ?paymentId=<uuid> (acrescentado pelo back) e os parâmetros do provedor
 * (order_nsu, transaction_nsu, slug, receipt_url, capture_method), pede a
 * verificação ao back e acompanha o status até CONFIRMED. Só exibe o status da API.
 */
export default function PaymentReturnPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { openOrderStatusModal } = useOrderStatus();
  const { getPayment, verifyReturn, startCheckout } = useOnlinePayment();

  const [payment, setPayment] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isPolling, setIsPolling] = useState(true);
  const [pollRound, setPollRound] = useState(0); // incrementa para reiniciar o polling
  const [isRetrying, setIsRetrying] = useState(false);
  const verifiedRef = useRef(false);

  const { paymentId: paramPaymentId, providerParams } = parseReturnParams(
    location.search
  );
  const paymentId = paramPaymentId || getLastOnlinePayment()?.paymentId;
  const hasProviderParams = !!(
    providerParams.transaction_nsu && providerParams.slug
  );

  // Rola para o topo ao abrir a página
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Verificação do retorno (uma vez) + polling do status
  useEffect(() => {
    if (!paymentId) {
      setIsPolling(false);
      return undefined;
    }

    let cancelled = false;
    let timer = null;
    let polls = 0;
    setIsPolling(true);

    const poll = async () => {
      if (cancelled) return;
      polls += 1;
      try {
        const data = await getPayment(paymentId);
        if (cancelled) return;
        setPayment(data);
        setErrorMessage("");
        if (FINAL_STATUSES.includes(data?.status)) {
          setIsPolling(false);
          return;
        }
      } catch (err) {
        if (cancelled) return;
        console.error("❌ Erro ao consultar pagamento online:", err);
        if (err?.status === 404) {
          setErrorMessage(
            err.data?.message || "Pagamento não encontrado nesta loja."
          );
          setIsPolling(false);
          return;
        }
      }
      if (polls >= MAX_POLLS) {
        setIsPolling(false);
        return;
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    const start = async () => {
      if (hasProviderParams && !verifiedRef.current) {
        verifiedRef.current = true;
        try {
          const data = await verifyReturn(paymentId, providerParams);
          if (cancelled) return;
          setPayment(data);
          if (FINAL_STATUSES.includes(data?.status)) {
            setIsPolling(false);
            return;
          }
        } catch (err) {
          // Segue para o polling: o back também confirma pelo webhook/worker
          console.error("⚠️ Falha ao verificar o retorno do pagamento:", err);
        }
      }
      poll();
    };

    start();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // providerParams é recalculado a cada render; location.search cobre a mudança
  },[paymentId, location.search, pollRound, getPayment, verifyReturn]);

  const orderCode =
    payment?.orderCode || getLastOnlinePayment()?.orderCode || "";
  const status = payment?.status;

  const handleTrackOrder = useCallback(() => {
    navigate("/");
    if (orderCode) {
      // Aguarda a navegação para abrir o modal de status (mesmo padrão do checkout)
      setTimeout(() => openOrderStatusModal(orderCode), 100);
    }
  }, [navigate, openOrderStatusModal, orderCode]);

  // Nova tentativa após UNKNOWN/CREATION_FAILED (ou retomar um PENDING)
  const handleRetry = async () => {
    if (!orderCode || isRetrying) return;
    setIsRetrying(true);
    try {
      const data = await startCheckout(orderCode, {
        newAttempt: RETRYABLE_STATUSES.includes(status),
      });
      saveLastOnlinePayment(data);
      if (data?.status === ONLINE_PAYMENT_STATUS.PENDING && data.checkoutUrl) {
        window.location.assign(data.checkoutUrl);
        return;
      }
      setPayment(data);
      setErrorMessage("");
      if (data?.paymentId && data.paymentId !== paymentId) {
        navigate(
          `/pagamento/retorno?paymentId=${encodeURIComponent(data.paymentId)}`,
          { replace: true }
        );
      } else {
        setPollRound((r) => r + 1);
      }
    } catch (err) {
      setErrorMessage(
        err?.data?.message ||
          "Não foi possível gerar um novo pagamento. Tente novamente."
      );
    } finally {
      setIsRetrying(false);
    }
  };

  const renderStatus = () => {
    if (!paymentId) {
      return (
        <StatusBlock
          icon={<AlertTriangle className="w-8 h-8 text-brand-600" />}
          title="Pagamento não identificado"
          text="Não encontramos os dados do pagamento. Se você já pagou, acompanhe o pedido ou fale com a loja."
        />
      );
    }

    if (errorMessage && !payment) {
      return (
        <StatusBlock
          icon={<AlertTriangle className="w-8 h-8 text-brand-600" />}
          title="Não foi possível consultar o pagamento"
          text={errorMessage}
        />
      );
    }

    if (!payment) {
      return (
        <StatusBlock
          icon={<Loader2 className="w-8 h-8 text-brand-600 animate-spin" />}
          title="Verificando seu pagamento..."
          text="Isso leva só alguns segundos."
        />
      );
    }

    switch (status) {
      case ONLINE_PAYMENT_STATUS.CONFIRMED:
        return (
          <StatusBlock
            icon={<CheckCircle2 className="w-8 h-8 text-success" />}
            title="Pagamento confirmado!"
            text={`Recebemos ${formatBRL(payment.amount)}${
              payment.providerName ? ` via ${payment.providerName}` : ""
            }. Seu pedido já está com a loja.`}
          />
        );
      case ONLINE_PAYMENT_STATUS.REVIEW:
        return (
          <StatusBlock
            icon={<Clock className="w-8 h-8 text-brand-600" />}
            title="Pagamento em análise"
            text={
              payment.message ||
              "A loja vai conferir seu pagamento. Em caso de dúvida, fale com a loja."
            }
          />
        );
      case ONLINE_PAYMENT_STATUS.UNKNOWN:
      case ONLINE_PAYMENT_STATUS.CREATION_FAILED:
        return (
          <StatusBlock
            icon={<AlertTriangle className="w-8 h-8 text-brand-600" />}
            title="Pagamento não concluído"
            text={payment.message || "Não foi possível gerar o pagamento."}
          />
        );
      default:
        // CREATING ou PENDING
        return (
          <StatusBlock
            icon={
              isPolling ? (
                <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
              ) : (
                <Clock className="w-8 h-8 text-brand-600" />
              )
            }
            title={
              isPolling ? "Aguardando a confirmação..." : "Pagamento em processamento"
            }
            text={
              isPolling
                ? "Assim que o pagamento for confirmado, esta tela será atualizada."
                : hasProviderParams
                ? "Ainda não recebemos a confirmação. Ela pode levar alguns minutos; você pode acompanhar pelo pedido."
                : "Ainda não identificamos o pagamento. Se não concluiu, volte para o pagamento."
            }
          />
        );
    }
  };

  const isPendingLike =
    status === ONLINE_PAYMENT_STATUS.PENDING ||
    status === ONLINE_PAYMENT_STATUS.CREATING;
  const canRetry = RETRYABLE_STATUSES.includes(status);

  return (
    <div className="min-h-screen bg-cream text-cocoa">
      <div className="bg-white border-b shadow-nav">
        <div className="max-w-xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="min-h-[44px] flex items-center gap-1 text-cocoa-muted hover:text-cocoa transition"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Cardápio</span>
          </button>
          <h1 className="text-lg font-serif font-bold">Pagamento</h1>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-card p-6 space-y-6">
          {renderStatus()}

          {orderCode && (
            <p className="text-center text-sm text-cocoa-muted">
              Pedido <span className="font-semibold text-cocoa">#{orderCode}</span>
            </p>
          )}

          {errorMessage && payment && (
            <p className="text-center text-sm text-red-600">{errorMessage}</p>
          )}

          <div className="space-y-3">
            {payment?.receiptUrl && (
              <a
                href={payment.receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full min-h-[44px] px-4 py-2 rounded-xl border border-brand-600/40 text-cocoa font-semibold hover:bg-cream transition flex items-center justify-center gap-2"
              >
                <Receipt className="w-4 h-4" />
                Ver comprovante
              </a>
            )}

            {canRetry && (
              <button
                type="button"
                onClick={handleRetry}
                disabled={isRetrying}
                className="w-full min-h-[44px] px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRetrying ? "Gerando pagamento..." : "Tentar novamente"}
              </button>
            )}

            {isPendingLike && !isPolling && (
              <>
                {payment?.checkoutUrl && (
                  <a
                    href={payment.checkoutUrl}
                    className="w-full min-h-[44px] px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold transition flex items-center justify-center"
                  >
                    Voltar para o pagamento
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => setPollRound((r) => r + 1)}
                  className="w-full min-h-[44px] px-4 py-2 rounded-xl border border-brand-600/40 text-cocoa font-semibold hover:bg-cream transition"
                >
                  Verificar novamente
                </button>
              </>
            )}

            {orderCode && (
              <button
                type="button"
                onClick={handleTrackOrder}
                className={
                  status === ONLINE_PAYMENT_STATUS.CONFIRMED
                    ? "w-full min-h-[44px] px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold transition"
                    : "w-full min-h-[44px] px-4 py-2 rounded-xl border border-brand-600/40 text-cocoa font-semibold hover:bg-cream transition"
                }
              >
                Acompanhar pedido
              </button>
            )}

            {!orderCode && (
              <button
                type="button"
                onClick={() => navigate("/")}
                className="w-full min-h-[44px] px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold transition"
              >
                Voltar ao cardápio
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBlock({ icon, title, text }) {
  return (
    <div className="flex flex-col items-center text-center gap-3">
      <div className="w-16 h-16 rounded-full bg-cream flex items-center justify-center">
        {icon}
      </div>
      <h2 className="text-xl font-serif font-bold text-cocoa">{title}</h2>
      {text && <p className="text-sm text-cocoa-muted max-w-sm">{text}</p>}
    </div>
  );
}
